import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Download, ExclamationTriangle, ShieldCheck } from 'react-bootstrap-icons';

import { getPartidasAuditoria } from '../../routes/Partidas/partidasService';
import PartidasTable from './PartidasTable';

export default function AuditoriaDespacho({ filters, refreshKey, onView }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getPartidasAuditoria(filters);
        if (active) setRows(Array.isArray(data) ? data : []);
      } catch (loadError) {
        if (active) {
          setRows([]);
          setError(loadError.message);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [filters, refreshKey]);

  const alertRows = useMemo(() => rows.filter((row) => row.alerta_despacho), [rows]);
  const alertArticles = useMemo(
    () => new Set(alertRows.map((row) => row.articulo_id)).size,
    [alertRows]
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const { exportPartidasExcel } = await import('../../utils/partidasExport');
      await exportPartidasExcel({ rows, filters, audit: true });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className='partidas-loading-state'>
        <Spinner animation='border' variant='primary' />
        <span>Analizando el proceso de despacho...</span>
      </div>
    );
  }

  return (
    <section className='partidas-results-card'>
      <div className='crm-results-header partidas-results-header'>
        <div>
          <span>Control FEFO actual</span>
          <h2>Auditoría de Proceso de Despacho</h2>
          <p>
            Reproduce la regla de Streamlit: detecta salidas en la partida con
            vencimiento máximo cuando existe otra anterior del mismo artículo.
          </p>
        </div>
        <Button variant='outline-primary' disabled={!rows.length || exporting} onClick={handleExport}>
          {exporting ? <Spinner size='sm' animation='border' /> : <Download />}
          Exportar auditoría
        </Button>
      </div>

      {error && <Alert variant='danger'>{error}</Alert>}

      {!error && alertRows.length > 0 && (
        <div className='partidas-audit-summary is-alert'>
          <ExclamationTriangle />
          <div>
            <strong>
              Se detectaron {alertRows.length} partidas posteriores con salida.
            </strong>
            <span>
              Corresponden a {alertArticles} artículo(s). Se muestran {rows.length}
              partidas para revisar el contexto completo.
            </span>
          </div>
        </div>
      )}

      {!error && alertRows.length === 0 && (
        <div className='partidas-audit-summary is-ok'>
          <ShieldCheck />
          <div>
            <strong>No se detectaron partidas posteriores con salida.</strong>
            <span>La auditoría no encontró alertas con los filtros actuales.</span>
          </div>
        </div>
      )}

      {!error && rows.length > 0 && (
        <PartidasTable
          rows={rows}
          total={rows.length}
          page={1}
          pageSize={rows.length}
          onView={onView}
          auditMode
        />
      )}
    </section>
  );
}
